/**
 * FilterStrip Component (Organism)
 *
 * Global Filter Strip - persistent, horizontal UI component for filtering records.
 * Filters apply immediately when selections change.
 */

import { useCallback } from 'react';
import { RotateCcw } from 'lucide-react';
import { cn } from '../../../../../lib/utils';
import { Button } from '../../../../ui/button';
import type {
  FilterStripProps,
  FilterConfig,
  FilterWithLogic,
  ConductDimension,
  RegulatoryFlag,
  ActiveFilterChip,
} from '../types/filter.types';

import { DateRangeFilter } from './DateRangeFilter';
import { ProductServiceFilter } from './ProductServiceFilter';
import { ChannelsFilter } from './ChannelsFilter';
import { JourneyStageFilter } from './JourneyStageFilter';
import { DimensionFilter } from './DimensionFilter';
import { RegulatoryFlagsFilter } from './RegulatoryFlagsFilter';
import { ActiveFilterChips } from './ActiveFilterChips';

export function FilterStrip({
  filters,
  onFiltersChange,
  onReset,
  isLoading = false,
  filterOptions,
  className,
}: FilterStripProps) {
  // Use provided options or defaults
  const productOptions = filterOptions?.productCategories || [];
  const channelOptions = filterOptions?.channels || [];
  const journeyOptions = filterOptions?.journeyStages || [];

  // Check if any filters are active
  const hasActiveFilters =
    filters.dateRange?.preset ||
    filters.dateRange?.from ||
    filters.dateRange?.to ||
    filters.businessContext?.productCategories?.values?.length ||
    filters.businessContext?.channels?.values?.length ||
    filters.businessContext?.journeyStages?.values?.length ||
    filters.dimensions?.values?.length ||
    filters.regulatoryFlags?.values?.length ||
    filters.search ||
    filters.conductScoreMin !== undefined ||
    filters.conductScoreMax !== undefined;

  // Handle date range change
  const handleDateRangeChange = useCallback(
    (dateRange: FilterConfig['dateRange']) => {
      onFiltersChange({ ...filters, dateRange });
    },
    [filters, onFiltersChange]
  );

  // Handle product categories change
  const handleProductsChange = useCallback(
    (productCategories: FilterWithLogic | undefined) => {
      onFiltersChange({
        ...filters,
        businessContext: {
          ...filters.businessContext,
          productCategories,
        },
      });
    },
    [filters, onFiltersChange]
  );

  // Handle channels change
  const handleChannelsChange = useCallback(
    (channels: FilterWithLogic | undefined) => {
      onFiltersChange({
        ...filters,
        businessContext: {
          ...filters.businessContext,
          channels,
        },
      });
    },
    [filters, onFiltersChange]
  );

  // Handle journey stages change
  const handleJourneysChange = useCallback(
    (journeyStages: FilterWithLogic | undefined) => {
      onFiltersChange({
        ...filters,
        businessContext: {
          ...filters.businessContext,
          journeyStages,
        },
      });
    },
    [filters, onFiltersChange]
  );

  // Handle dimensions change
  const handleDimensionsChange = useCallback(
    (dimensions: FilterWithLogic<ConductDimension> | undefined) => {
      onFiltersChange({ ...filters, dimensions });
    },
    [filters, onFiltersChange]
  );

  // Handle regulatory flags change
  const handleRegulatoryFlagsChange = useCallback(
    (regulatoryFlags: FilterWithLogic<RegulatoryFlag> | undefined) => {
      onFiltersChange({ ...filters, regulatoryFlags });
    },
    [filters, onFiltersChange]
  );

  // Handle removing a single filter chip
  const handleRemoveFilter = useCallback(
    (type: ActiveFilterChip['type'], value?: string) => {
      const updated = { ...filters };

      switch (type) {
        case 'dateRange':
          delete updated.dateRange;
          break;
        case 'product':
          if (updated.businessContext?.productCategories?.values) {
            const filtered = updated.businessContext.productCategories.values.filter(
              (v) => v !== value
            );
            updated.businessContext = {
              ...updated.businessContext,
              productCategories: filtered.length > 0
                ? { ...updated.businessContext.productCategories, values: filtered }
                : undefined,
            };
          }
          break;
        case 'channel':
          if (updated.businessContext?.channels?.values) {
            const filtered = updated.businessContext.channels.values.filter(
              (v) => v !== value
            );
            updated.businessContext = {
              ...updated.businessContext,
              channels: filtered.length > 0
                ? { ...updated.businessContext.channels, values: filtered }
                : undefined,
            };
          }
          break;
        case 'journey':
          if (updated.businessContext?.journeyStages?.values) {
            const filtered = updated.businessContext.journeyStages.values.filter(
              (v) => v !== value
            );
            updated.businessContext = {
              ...updated.businessContext,
              journeyStages: filtered.length > 0
                ? { ...updated.businessContext.journeyStages, values: filtered }
                : undefined,
            };
          }
          break;
        case 'dimension':
          if (updated.dimensions?.values) {
            const filtered = updated.dimensions.values.filter((v) => v !== value);
            if (filtered.length === 0) {
              delete updated.dimensions;
            } else {
              updated.dimensions = { ...updated.dimensions, values: filtered };
            }
          }
          break;
        case 'regulatory':
          if (updated.regulatoryFlags?.values) {
            const filtered = updated.regulatoryFlags.values.filter((v) => v !== value);
            if (filtered.length === 0) {
              delete updated.regulatoryFlags;
            } else {
              updated.regulatoryFlags = { ...updated.regulatoryFlags, values: filtered };
            }
          }
          break;
        case 'search':
          delete updated.search;
          break;
        case 'conductScore':
          delete updated.conductScoreMin;
          delete updated.conductScoreMax;
          break;
      }

      // Clean up empty businessContext
      if (
        updated.businessContext &&
        !updated.businessContext.productCategories &&
        !updated.businessContext.channels &&
        !updated.businessContext.journeyStages
      ) {
        delete updated.businessContext;
      }

      onFiltersChange(updated);
    },
    [filters, onFiltersChange]
  );

  return (
    <div
      className={cn('space-y-4', className)}
      data-testid="filter-strip"
    >
      {/* Filter Controls Row */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Date Range */}
        <DateRangeFilter
          value={filters.dateRange}
          onChange={handleDateRangeChange}
          disabled={isLoading}
          className="w-[180px]"
        />

        {/* Products (only show if options available) */}
        {productOptions.length > 0 && (
          <ProductServiceFilter
            value={filters.businessContext?.productCategories}
            onChange={handleProductsChange}
            options={productOptions}
            disabled={isLoading}
            className="w-[180px]"
          />
        )}

        {/* Channels (only show if options available) */}
        {channelOptions.length > 0 && (
          <ChannelsFilter
            value={filters.businessContext?.channels}
            onChange={handleChannelsChange}
            options={channelOptions}
            disabled={isLoading}
            className="w-[180px]"
          />
        )}

        {/* Journey Stages (only show if options available) */}
        {journeyOptions.length > 0 && (
          <JourneyStageFilter
            value={filters.businessContext?.journeyStages}
            onChange={handleJourneysChange}
            options={journeyOptions}
            disabled={isLoading}
            className="w-[180px]"
          />
        )}

        {/* Dimensions */}
        <DimensionFilter
          value={filters.dimensions}
          onChange={handleDimensionsChange}
          disabled={isLoading}
          className="w-[180px]"
        />

        {/* Regulatory Flags */}
        <RegulatoryFlagsFilter
          value={filters.regulatoryFlags}
          onChange={handleRegulatoryFlagsChange}
          disabled={isLoading}
          className="w-[180px]"
        />

        {/* Spacer */}
        <div className="flex-1" />

        {/* Reset Button */}
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onReset}
            disabled={isLoading}
            className="rounded-full text-muted-foreground"
          >
            <RotateCcw className="mr-1 h-4 w-4" />
            Reset
          </Button>
        )}
      </div>

      {/* Active Filter Chips */}
      <ActiveFilterChips
        filters={filters}
        onRemoveFilter={handleRemoveFilter}
      />
    </div>
  );
}
